import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { createMap } from './map/mapController.js'
import { createRoom, getRoomByCode } from './lib/api.js'

export default function Landing() {
  const navigate = useNavigate()
  const mapDivRef = useRef(null)
  const [mode, setMode] = useState('home') // home | start | joinCode
  const [code, setCode] = useState('')
  const [maxElements, setMaxElements] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  // Dimmed, non-interactive background map with a slow auto-pan so it feels alive.
  useEffect(() => {
    const { map } = createMap(mapDivRef.current, { interactive: false, center: [25, 10], zoom: 3 })
    let lng = 10
    const id = setInterval(() => {
      lng += 0.8
      if (lng > 180) lng -= 360
      map.panTo([25, lng], { animate: true, duration: 2.4, easeLinearity: 1 })
    }, 2500)
    return () => {
      clearInterval(id)
      map.remove()
    }
  }, [])

  // Create the room with its settings; the Room screen prompts for name/color.
  async function handleStart(e) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      const n = Number.parseInt(maxElements, 10)
      const room = await createRoom(null, Number.isInteger(n) && n >= 1 ? n : null)
      navigate(`/room/${room.id}`)
    } catch {
      setError('Could not create a room. Try again.')
      setBusy(false)
    }
  }

  async function handleJoinCode(e) {
    e.preventDefault()
    const c = code.trim().toUpperCase()
    if (!c) return
    setBusy(true)
    setError('')
    try {
      const room = await getRoomByCode(c)
      if (!room) {
        setError('No room with that code.')
        setBusy(false)
        return
      }
      // The Room screen asks for name/color on entry.
      navigate(`/room/${room.id}`)
    } catch {
      setError('Lookup failed. Try again.')
      setBusy(false)
    }
  }

  return (
    <div className="landing">
      <div ref={mapDivRef} className="landing-map" />
      <div className="landing-scrim" />

      <div className="landing-content">
        <div className="brand">
          <h1>Collaborative Map</h1>
          <p>Drop points, draw, and measure together — live.</p>
        </div>

        {mode === 'home' && (
          <div className="card">
            {error && <div className="error">{error}</div>}
            <button
              className="btn-primary big"
              disabled={busy}
              onClick={() => {
                setMode('start')
                setError('')
              }}
            >
              Start a Map Room
            </button>
            <button
              className="btn-secondary big"
              disabled={busy}
              onClick={() => {
                setMode('joinCode')
                setError('')
              }}
            >
              Join a Map Room
            </button>
          </div>
        )}

        {mode === 'start' && (
          <form className="card" onSubmit={handleStart}>
            {error && <div className="error">{error}</div>}
            <label className="field">
              <span>Max elements per person (optional)</span>
              <input
                type="number"
                min="1"
                max="1000"
                value={maxElements}
                onChange={(e) => setMaxElements(e.target.value)}
                placeholder="Leave blank for unlimited"
                autoFocus
              />
            </label>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-ghost"
                onClick={() => {
                  setMode('home')
                  setError('')
                }}
              >
                Back
              </button>
              <button type="submit" className="btn-primary" disabled={busy}>
                Create room
              </button>
            </div>
          </form>
        )}

        {mode === 'joinCode' && (
          <form className="card" onSubmit={handleJoinCode}>
            {error && <div className="error">{error}</div>}
            <label className="field">
              <span>Room code</span>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="MAP-XXXX"
                autoFocus
              />
            </label>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-ghost"
                onClick={() => {
                  setMode('home')
                  setError('')
                }}
              >
                Back
              </button>
              <button type="submit" className="btn-primary" disabled={busy || !code.trim()}>
                Join
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
